<template>
  <div class="pipeline-status">
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <h1 class="text-h4 mb-4">
            <v-icon left large color="primary">mdi-pipe</v-icon>
            Pipeline Status
          </h1>
        </v-col>
      </v-row>

      <!-- Error Alert -->
      <v-row v-if="errorMessage">
        <v-col cols="12">
          <v-alert type="error" dismissible @input="errorMessage = null">
            {{ errorMessage }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- Refresh Button -->
      <v-row>
        <v-col cols="12">
          <v-btn 
            color="primary" 
            @click="refreshAll" 
            :loading="isLoading"
            class="mb-4"
          >
            <v-icon left>mdi-refresh</v-icon>
            Alle aktualisieren
          </v-btn>
          <span v-if="lastRefresh" class="ml-4 text-caption grey--text">
            Letzte Aktualisierung: {{ lastRefresh }}
          </span>
        </v-col>
      </v-row>

      <!-- Aktueller Deployment-Status pro Stage -->
      <v-row v-if="deployedReleases.length > 0" class="mb-4">
        <v-col cols="12">
          <v-card>
            <v-card-title class="pipeline-section-header">
              <v-icon left color="#281e78">mdi-server</v-icon>
              Aktuelle Deployments pro Umgebung
            </v-card-title>
            <v-card-text class="pa-4">
              <v-row>
                <v-col 
                  v-for="deployment in deployedReleases" 
                  :key="deployment.environment" 
                  cols="12" 
                  md="6"
                >
                  <v-card outlined class="deployment-card">
                    <v-card-title class="py-2">
                      <v-icon left :color="deployment.status === 'succeeded' ? 'success' : 'warning'">
                        {{ deployment.status === 'succeeded' ? 'mdi-check-circle' : 'mdi-progress-clock' }}
                      </v-icon>
                      {{ deployment.environment }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex align-center justify-space-between">
                        <div>
                          <div class="text-subtitle-1 font-weight-bold">
                            {{ deployment.releaseName }}
                          </div>
                          <div class="text-caption grey--text">
                            {{ deployment.definitionName }}
                          </div>
                          <div class="text-caption mt-1">
                            <v-icon x-small>mdi-calendar</v-icon>
                            Deployed: {{ formatDate(deployment.deployedOn) }}
                          </div>
                          <div v-if="deployment.deployedBy" class="text-caption">
                            <v-icon x-small>mdi-account</v-icon>
                            {{ deployment.deployedBy }}
                          </div>
                        </div>
                        <div>
                          <v-chip 
                            :color="getEnvStatusColor(deployment.status)" 
                            dark 
                            small
                          >
                            {{ deployment.status }}
                          </v-chip>
                        </div>
                      </div>
                    </v-card-text>
                    <v-card-actions class="pt-0">
                      <v-btn 
                        text 
                        small 
                        color="primary" 
                        @click="openPipelineUrl(deployment.url)"
                      >
                        <v-icon left small>mdi-open-in-new</v-icon>
                        In Azure DevOps öffnen
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Build Pipelines -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title class="pipeline-section-header">
              <v-icon left color="#281e78">mdi-hammer-wrench</v-icon>
              Build Pipelines ({{ buildPipelines.length }})
            </v-card-title>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="buildHeaders"
                :items="buildPipelines"
                :loading="loadingBuilds"
                :items-per-page="10"
                :expanded.sync="expandedBuilds"
                item-key="id"
                show-expand
                loading-text="Lade Build-Pipelines von Azure DevOps..."
                no-data-text="Keine Build-Pipelines gefunden"
                class="elevation-0"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    dark
                    small
                  >
                    <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:item.result="{ item }">
                  <v-chip
                    v-if="item.result"
                    :color="getResultColor(item.result)"
                    dark
                    small
                  >
                    {{ item.result }}
                  </v-chip>
                  <span v-else class="grey--text">-</span>
                </template>
                <template v-slot:item.startTime="{ item }">
                  {{ formatDate(item.startTime) }}
                </template>
                <template v-slot:item.finishTime="{ item }">
                  {{ formatDate(item.finishTime) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn 
                    icon 
                    small 
                    @click="openPipelineUrl(item.url)"
                    title="In Azure DevOps öffnen"
                  >
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                <!-- Expanded Row für Build Details -->
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pa-0">
                    <div class="details-panel pa-4 grey lighten-4">
                      <v-progress-linear v-if="buildDetailsLoading[item.id]" indeterminate color="primary" class="mb-4"></v-progress-linear>
                      
                      <v-row v-if="!buildDetailsLoading[item.id]">
                        <!-- Work Items -->
                        <v-col cols="12" md="6">
                          <h4 class="text-subtitle-1 font-weight-bold mb-2">
                            <v-icon left small color="primary">mdi-ticket</v-icon>
                            Zugeordnete Work-Items ({{ getBuildWorkItems(item.id).length }})
                          </h4>
                          <v-card v-if="getBuildWorkItems(item.id).length > 0" class="mb-2" outlined>
                            <v-list dense>
                              <v-list-item 
                                v-for="wi in getBuildWorkItems(item.id)" 
                                :key="wi.id"
                                @click="openPipelineUrl(wi.url)"
                                class="work-item-row"
                              >
                                <v-list-item-icon class="mr-2">
                                  <v-icon :color="getWorkItemTypeColor(wi.workItemType)" small>
                                    {{ getWorkItemTypeIcon(wi.workItemType) }}
                                  </v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="text-body-2">
                                    <span class="font-weight-medium">#{{ wi.id }}</span> - {{ wi.title }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle class="text-caption">
                                    <v-chip x-small :color="getWorkItemTypeColor(wi.workItemType)" dark class="mr-1">
                                      {{ wi.workItemType }}
                                    </v-chip>
                                    <span class="grey--text">{{ wi.state }}</span>
                                    <span v-if="wi.assignedTo !== '-'" class="ml-2 grey--text">
                                      <v-icon x-small>mdi-account</v-icon> {{ wi.assignedTo }}
                                    </span>
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                  <v-icon small>mdi-open-in-new</v-icon>
                                </v-list-item-action>
                              </v-list-item>
                            </v-list>
                          </v-card>
                          <v-alert v-else type="info" dense text class="text-body-2">
                            Keine Work-Items zugeordnet
                          </v-alert>
                        </v-col>
                        
                        <!-- Commits/Changes -->
                        <v-col cols="12" md="6">
                          <h4 class="text-subtitle-1 font-weight-bold mb-2">
                            <v-icon left small color="orange">mdi-source-commit</v-icon>
                            Commits / Änderungen ({{ getBuildChanges(item.id).length }})
                          </h4>
                          <v-card v-if="getBuildChanges(item.id).length > 0" class="mb-2" outlined>
                            <v-list dense>
                              <v-list-item 
                                v-for="(change, idx) in getBuildChanges(item.id)" 
                                :key="idx"
                                @click="openPipelineUrl(change.displayUri)"
                                class="commit-row"
                              >
                                <v-list-item-icon class="mr-2">
                                  <v-icon color="orange" small>mdi-source-commit</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="text-body-2 commit-message">
                                    {{ change.message }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle class="text-caption">
                                    <v-icon x-small>mdi-account</v-icon> {{ change.author }}
                                    <span class="ml-2">
                                      <v-icon x-small>mdi-clock-outline</v-icon> {{ formatDate(change.timestamp) }}
                                    </span>
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="change.displayUri">
                                  <v-icon small>mdi-open-in-new</v-icon>
                                </v-list-item-action>
                              </v-list-item>
                            </v-list>
                          </v-card>
                          <v-alert v-else type="info" dense text class="text-body-2">
                            Keine Commits/Änderungen gefunden
                          </v-alert>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Release Pipelines -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card>
            <v-card-title class="pipeline-section-header">
              <v-icon left color="#281e78">mdi-rocket-launch</v-icon>
              Release Pipelines ({{ releasePipelines.length }})
            </v-card-title>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="releaseHeaders"
                :items="releasePipelines"
                :loading="loadingReleases"
                :items-per-page="10"
                :expanded.sync="expandedReleases"
                item-key="id"
                show-expand
                loading-text="Lade Release-Pipelines von Azure DevOps..."
                no-data-text="Keine Release-Pipelines gefunden"
                class="elevation-0"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    dark
                    small
                  >
                    <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:item.environments="{ item }">
                  <v-chip
                    v-for="(env, idx) in item.environments"
                    :key="idx"
                    :color="getEnvStatusColor(env.status)"
                    dark
                    small
                    class="mr-1 mb-1"
                  >
                    {{ env.name }}: {{ env.status }}
                  </v-chip>
                </template>
                <template v-slot:item.createdOn="{ item }">
                  {{ formatDate(item.createdOn) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn 
                    icon 
                    small 
                    @click="openPipelineUrl(item.url)"
                    title="In Azure DevOps öffnen"
                  >
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                <!-- Expanded Row für Release Details -->
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pa-0">
                    <div class="details-panel pa-4 grey lighten-4">
                      <v-progress-linear v-if="releaseDetailsLoading[item.id]" indeterminate color="success" class="mb-4"></v-progress-linear>
                      
                      <v-row v-if="!releaseDetailsLoading[item.id]">
                        <!-- Work Items -->
                        <v-col cols="12" md="6">
                          <h4 class="text-subtitle-1 font-weight-bold mb-2">
                            <v-icon left small color="primary">mdi-ticket</v-icon>
                            Zugeordnete Work-Items ({{ getReleaseWorkItems(item.id).length }})
                          </h4>
                          <v-card v-if="getReleaseWorkItems(item.id).length > 0" class="mb-2" outlined>
                            <v-list dense>
                              <v-list-item 
                                v-for="wi in getReleaseWorkItems(item.id)" 
                                :key="wi.id"
                                @click="openPipelineUrl(wi.url)"
                                class="work-item-row"
                              >
                                <v-list-item-icon class="mr-2">
                                  <v-icon :color="getWorkItemTypeColor(wi.workItemType)" small>
                                    {{ getWorkItemTypeIcon(wi.workItemType) }}
                                  </v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="text-body-2">
                                    <span class="font-weight-medium">#{{ wi.id }}</span> - {{ wi.title }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle class="text-caption">
                                    <v-chip x-small :color="getWorkItemTypeColor(wi.workItemType)" dark class="mr-1">
                                      {{ wi.workItemType }}
                                    </v-chip>
                                    <span class="grey--text">{{ wi.state }}</span>
                                    <span v-if="wi.assignedTo !== '-'" class="ml-2 grey--text">
                                      <v-icon x-small>mdi-account</v-icon> {{ wi.assignedTo }}
                                    </span>
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                  <v-icon small>mdi-open-in-new</v-icon>
                                </v-list-item-action>
                              </v-list-item>
                            </v-list>
                          </v-card>
                          <v-alert v-else type="info" dense text class="text-body-2">
                            Keine Work-Items zugeordnet
                          </v-alert>
                        </v-col>
                        
                        <!-- Commits/Changes -->
                        <v-col cols="12" md="6">
                          <h4 class="text-subtitle-1 font-weight-bold mb-2">
                            <v-icon left small color="orange">mdi-source-commit</v-icon>
                            Commits / Änderungen ({{ getReleaseChanges(item.id).length }})
                          </h4>
                          <v-card v-if="getReleaseChanges(item.id).length > 0" class="mb-2" outlined>
                            <v-list dense>
                              <v-list-item 
                                v-for="(change, idx) in getReleaseChanges(item.id)" 
                                :key="idx"
                                @click="openPipelineUrl(change.displayUri)"
                                class="commit-row"
                              >
                                <v-list-item-icon class="mr-2">
                                  <v-icon color="orange" small>mdi-source-commit</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="text-body-2 commit-message">
                                    {{ change.message }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle class="text-caption">
                                    <v-icon x-small>mdi-account</v-icon> {{ change.author }}
                                    <span class="ml-2">
                                      <v-icon x-small>mdi-clock-outline</v-icon> {{ formatDate(change.timestamp) }}
                                    </span>
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="change.displayUri">
                                  <v-icon small>mdi-open-in-new</v-icon>
                                </v-list-item-action>
                              </v-list-item>
                            </v-list>
                          </v-card>
                          <v-alert v-else type="info" dense text class="text-body-2">
                            Keine Commits/Änderungen gefunden
                          </v-alert>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Pipeline Zusammenfassung -->
      <v-row class="mt-4">
        <v-col cols="12" md="4">
          <v-card class="text-center pa-4">
            <v-icon size="48" :color="summaryStats.buildsSuccess > 0 ? '#66bb6a' : '#bdbdbd'">
              mdi-check-circle
            </v-icon>
            <h2 class="text-h4 mt-2">{{ summaryStats.buildsSuccess }}</h2>
            <p class="text-subtitle-1 grey--text">Erfolgreiche Builds</p>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="text-center pa-4">
            <v-icon size="48" :color="summaryStats.buildsFailed > 0 ? '#ef5350' : '#bdbdbd'">
              mdi-alert-circle
            </v-icon>
            <h2 class="text-h4 mt-2">{{ summaryStats.buildsFailed }}</h2>
            <p class="text-subtitle-1 grey--text">Fehlgeschlagene Builds</p>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="text-center pa-4">
            <v-icon size="48" :color="summaryStats.buildsInProgress > 0 ? '#ffa726' : '#bdbdbd'">
              mdi-progress-clock
            </v-icon>
            <h2 class="text-h4 mt-2">{{ summaryStats.buildsInProgress }}</h2>
            <p class="text-subtitle-1 grey--text">Laufende Builds</p>
          </v-card>
        </v-col>
      </v-row>

      <!-- API Fehler Info -->
      <v-row v-if="apiErrors" class="mt-4">
        <v-col cols="12">
          <v-alert type="warning" outlined>
            <strong>API Hinweise:</strong>
            <ul>
              <li v-if="apiErrors.builds">Builds: {{ apiErrors.builds }}</li>
              <li v-if="apiErrors.releases">Releases: {{ apiErrors.releases }}</li>
              <li v-if="apiErrors.buildDefinitions">Build-Definitionen: {{ apiErrors.buildDefinitions }}</li>
              <li v-if="apiErrors.releaseDefinitions">Release-Definitionen: {{ apiErrors.releaseDefinitions }}</li>
            </ul>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Konfiguration Info -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left>mdi-information</v-icon>
                Konfigurationshinweise
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p>Diese Seite zeigt den Status der Azure DevOps Pipelines für das Energyview-Projekt.</p>
                <p><strong>Konfiguration in <code>projectSettings.json</code>:</strong></p>
                <ul>
                  <li>Azure DevOps Organisation: <code>NEW-MG</code></li>
                  <li>Projekt: <code>Energyview</code></li>
                  <li><code>AZURE_DEVOPS_PAT</code>: Personal Access Token mit Lese-Berechtigung für Build und Release</li>
                </ul>
                <p class="mt-2"><strong>PAT-Berechtigungen:</strong></p>
                <ul>
                  <li>Build (Read)</li>
                  <li>Release (Read)</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

    </v-container>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from "vue"
import { httpsCallable } from "firebase/functions"
import { functions } from "@/plugins/firebase"
import router from "../router"
import store from "../store"

export default defineComponent({
  name: "PipelineStatus",
  
  setup() {
    const isLoading = ref(false)
    const loadingBuilds = ref(false)
    const loadingReleases = ref(false)
    const lastRefresh = ref(null)
    const errorMessage = ref(null)
    const apiErrors = ref(null)
    
    // Build Pipelines Daten
    const buildPipelines = ref([])
    const expandedBuilds = ref([])
    const buildDetails = ref({})
    const buildDetailsLoading = ref({})
    
    // Release Pipelines Daten
    const releasePipelines = ref([])
    const expandedReleases = ref([])
    const releaseDetails = ref({})
    const releaseDetailsLoading = ref({})
    
    // Tabellen-Header für Builds
    const buildHeaders = [
      { text: "Pipeline", value: "name", sortable: true },
      { text: "Build #", value: "buildNumber", sortable: true },
      { text: "Branch", value: "branch", sortable: true },
      { text: "Status", value: "status", sortable: true },
      { text: "Ergebnis", value: "result", sortable: true },
      { text: "Gestartet", value: "startTime", sortable: true },
      { text: "Beendet", value: "finishTime", sortable: true },
      { text: "Auslöser", value: "requestedFor", sortable: false },
      { text: "Aktionen", value: "actions", sortable: false }
    ]
    
    // Tabellen-Header für Releases
    const releaseHeaders = [
      { text: "Release", value: "name", sortable: true },
      { text: "Definition", value: "definitionName", sortable: true },
      { text: "Status", value: "status", sortable: true },
      { text: "Umgebungen", value: "environments", sortable: false },
      { text: "Erstellt", value: "createdOn", sortable: true },
      { text: "Erstellt von", value: "createdBy", sortable: false },
      { text: "Aktionen", value: "actions", sortable: false }
    ]
    
    // Zusammenfassung berechnen
    const summaryStats = computed(() => {
      return {
        buildsSuccess: buildPipelines.value.filter(b => b.result === "succeeded").length,
        buildsFailed: buildPipelines.value.filter(b => b.result === "failed").length,
        buildsInProgress: buildPipelines.value.filter(b => b.status === "inProgress").length
      }
    })
    
    // Aktuell deploytes Release pro Umgebung ermitteln
    const deployedReleases = computed(() => {
      // Sammle alle Umgebungen mit ihrem neuesten erfolgreichen Release
      const envMap = {}
      
      for (const release of releasePipelines.value) {
        if (!release.environments) continue
        
        for (const env of release.environments) {
          // Nur erfolgreich deployete oder gerade laufende Umgebungen
          if (env.status !== "succeeded" && env.status !== "inProgress") continue
          
          const envName = env.name
          
          // Wenn wir diese Umgebung noch nicht haben, oder das Release neuer ist
          if (!envMap[envName] || new Date(release.createdOn) > new Date(envMap[envName].createdOn)) {
            envMap[envName] = {
              environment: envName,
              releaseName: release.name,
              definitionName: release.definitionName,
              releaseId: release.id,
              status: env.status,
              deployedOn: release.createdOn,
              deployedBy: release.createdBy,
              url: release.url,
              createdOn: release.createdOn
            }
          }
        }
      }
      
      // Sortiere nach Umgebungsnamen (firebase staging vor firebase release)
      return Object.values(envMap).sort((a, b) => {
        // Staging soll zuerst kommen
        if (a.environment.toLowerCase().includes("staging")) return -1
        if (b.environment.toLowerCase().includes("staging")) return 1
        return a.environment.localeCompare(b.environment)
      })
    })
    
    // Status-Farben
    const getStatusColor = (status) => {
      const colors = {
        "completed": "#4caf50",
        "succeeded": "#4caf50",
        "inProgress": "#ff9800",
        "running": "#ff9800",
        "queued": "#90a4ae",
        "notStarted": "#bdbdbd",
        "notDeployed": "#bdbdbd",
        "failed": "#ef5350",
        "canceled": "#9e9e9e",
        "partiallySucceeded": "#ffb74d",
        "active": "#42a5f5",
        "abandoned": "#9e9e9e",
        "undefined": "#bdbdbd"
      }
      return colors[status] || "#bdbdbd"
    }
    
    const getResultColor = (result) => {
      const colors = {
        "succeeded": "#4caf50",
        "failed": "#ef5350",
        "canceled": "#9e9e9e",
        "partiallySucceeded": "#ffb74d"
      }
      return colors[result] || "#bdbdbd"
    }
    
    const getEnvStatusColor = (status) => {
      const colors = {
        "succeeded": "#4caf50",
        "inProgress": "#ff9800",
        "notStarted": "#bdbdbd",
        "notDeployed": "#bdbdbd",
        "rejected": "#ef5350",
        "canceled": "#9e9e9e",
        "queued": "#90a4ae",
        "scheduled": "#42a5f5"
      }
      return colors[status] || "#bdbdbd"
    }
    
    // Status-Icons
    const getStatusIcon = (status) => {
      const icons = {
        "completed": "mdi-check-circle",
        "succeeded": "mdi-check-circle",
        "inProgress": "mdi-progress-clock",
        "running": "mdi-progress-clock",
        "queued": "mdi-clock-outline",
        "notStarted": "mdi-circle-outline",
        "notDeployed": "mdi-circle-outline",
        "failed": "mdi-alert-circle",
        "canceled": "mdi-cancel",
        "partiallySucceeded": "mdi-alert",
        "active": "mdi-play-circle",
        "abandoned": "mdi-close-circle",
        "undefined": "mdi-help-circle"
      }
      return icons[status] || "mdi-help-circle"
    }
    
    // Work Item Type Icons
    const getWorkItemTypeIcon = (type) => {
      const icons = {
        "Bug": "mdi-bug",
        "Task": "mdi-checkbox-marked-outline",
        "User Story": "mdi-book-open-page-variant",
        "Feature": "mdi-star",
        "Epic": "mdi-lightning-bolt",
        "Issue": "mdi-alert-circle"
      }
      return icons[type] || "mdi-file-document"
    }
    
    // Work Item Type Colors
    const getWorkItemTypeColor = (type) => {
      const colors = {
        "Bug": "error",
        "Task": "info",
        "User Story": "success",
        "Feature": "purple",
        "Epic": "orange",
        "Issue": "warning"
      }
      return colors[type] || "grey"
    }
    
    // Datum formatieren
    const formatDate = (dateString) => {
      if (!dateString) return "-"
      const date = new Date(dateString)
      return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    }
    
    // URL öffnen
    const openPipelineUrl = (url) => {
      if (url) {
        window.open(url, "_blank")
      }
    }
    
    // Daten von Azure DevOps laden
    const loadPipelineData = async () => {
      isLoading.value = true
      loadingBuilds.value = true
      loadingReleases.value = true
      errorMessage.value = null
      apiErrors.value = null
      
      try {
        // Cloud Function aufrufen
        const getPipelineStatus = httpsCallable(functions, "getPipelineStatus")
        const result = await getPipelineStatus({ top: 30 })
        
        if (result.data.success) {
          buildPipelines.value = result.data.builds || []
          // Nur "Energyview NG" Release-Pipeline anzeigen
          releasePipelines.value = (result.data.releases || []).filter(
            release => release.definitionName === "Energyview NG"
          )
          
          // API Fehler anzeigen falls vorhanden
          if (result.data.errors) {
            const hasErrors = Object.values(result.data.errors).some(e => e !== null)
            if (hasErrors) {
              apiErrors.value = result.data.errors
            }
          }
        } else {
          errorMessage.value = result.data.error || "Unbekannter Fehler beim Abrufen der Pipeline-Daten"
          buildPipelines.value = []
          releasePipelines.value = []
        }
        
        lastRefresh.value = new Date().toLocaleString("de-DE")
      } catch (error) {
        console.error("Fehler beim Laden der Pipeline-Daten:", error)
        errorMessage.value = error.message || "Fehler beim Abrufen der Pipeline-Daten"
        buildPipelines.value = []
        releasePipelines.value = []
      } finally {
        isLoading.value = false
        loadingBuilds.value = false
        loadingReleases.value = false
      }
    }
    
    const refreshAll = async () => {
      await loadPipelineData()
    }
    
    // Build Details laden (Work-Items und Commits)
    const loadBuildDetails = async (buildId) => {
      if (buildDetails.value[buildId]) {
        // Bereits geladen
        return
      }
      
      buildDetailsLoading.value = { ...buildDetailsLoading.value, [buildId]: true }
      
      try {
        const getBuildDetailsFunc = httpsCallable(functions, "getBuildDetails")
        const result = await getBuildDetailsFunc({ buildId })
        
        if (result.data.success) {
          buildDetails.value = {
            ...buildDetails.value,
            [buildId]: {
              workItems: result.data.workItems || [],
              changes: result.data.changes || []
            }
          }
        } else {
          console.error("Fehler beim Laden der Build-Details:", result.data.error)
        }
      } catch (error) {
        console.error("Fehler beim Laden der Build-Details:", error)
      } finally {
        buildDetailsLoading.value = { ...buildDetailsLoading.value, [buildId]: false }
      }
    }
    
    // Release Details laden (Work-Items und Commits)
    const loadReleaseDetails = async (releaseId) => {
      if (releaseDetails.value[releaseId]) {
        // Bereits geladen
        return
      }
      
      releaseDetailsLoading.value = { ...releaseDetailsLoading.value, [releaseId]: true }
      
      try {
        const getReleaseDetailsFunc = httpsCallable(functions, "getReleaseDetails")
        const result = await getReleaseDetailsFunc({ releaseId })
        
        if (result.data.success) {
          releaseDetails.value = {
            ...releaseDetails.value,
            [releaseId]: {
              workItems: result.data.workItems || [],
              changes: result.data.changes || []
            }
          }
        } else {
          console.error("Fehler beim Laden der Release-Details:", result.data.error)
        }
      } catch (error) {
        console.error("Fehler beim Laden der Release-Details:", error)
      } finally {
        releaseDetailsLoading.value = { ...releaseDetailsLoading.value, [releaseId]: false }
      }
    }
    
    // Watch für expandedBuilds - lade Details wenn erweitert
    watch(expandedBuilds, (newExpanded) => {
      newExpanded.forEach(item => {
        if (!buildDetails.value[item.id] && !buildDetailsLoading.value[item.id]) {
          loadBuildDetails(item.id)
        }
      })
    })
    
    // Watch für expandedReleases - lade Details wenn erweitert
    watch(expandedReleases, (newExpanded) => {
      newExpanded.forEach(item => {
        if (!releaseDetails.value[item.id] && !releaseDetailsLoading.value[item.id]) {
          loadReleaseDetails(item.id)
        }
      })
    })
    
    // Hilfsmethoden für Template (vermeidet Optional Chaining im Template)
    const getBuildWorkItems = (buildId) => {
      const details = buildDetails.value[buildId]
      return details && details.workItems ? details.workItems : []
    }
    
    const getBuildChanges = (buildId) => {
      const details = buildDetails.value[buildId]
      return details && details.changes ? details.changes : []
    }
    
    const getReleaseWorkItems = (releaseId) => {
      const details = releaseDetails.value[releaseId]
      return details && details.workItems ? details.workItems : []
    }
    
    const getReleaseChanges = (releaseId) => {
      const details = releaseDetails.value[releaseId]
      return details && details.changes ? details.changes : []
    }
    
    // Berechtigungsprüfung beim Mounten
    onMounted(async () => {
      // Prüfe ob User eingeloggt ist
      if (!store.state.auth.isSignedIn) {
        router.push("/login")
        return
      }
      
      // Prüfe Admin-Berechtigung
      const authClaims = store.state.auth.authClaims
      if (!authClaims || !authClaims.permissions || !authClaims.permissions.admin) {
        router.push("/")
        return
      }
      
      // Daten laden
      await loadPipelineData()
    })
    
    return {
      isLoading,
      loadingBuilds,
      loadingReleases,
      lastRefresh,
      errorMessage,
      apiErrors,
      buildPipelines,
      releasePipelines,
      buildHeaders,
      releaseHeaders,
      summaryStats,
      deployedReleases,
      getStatusColor,
      getResultColor,
      getEnvStatusColor,
      getStatusIcon,
      getWorkItemTypeIcon,
      getWorkItemTypeColor,
      formatDate,
      openPipelineUrl,
      refreshAll,
      // Neue Eigenschaften für erweiterbare Details
      expandedBuilds,
      expandedReleases,
      buildDetails,
      releaseDetails,
      buildDetailsLoading,
      releaseDetailsLoading,
      loadBuildDetails,
      loadReleaseDetails,
      // Hilfsmethoden für Template
      getBuildWorkItems,
      getBuildChanges,
      getReleaseWorkItems,
      getReleaseChanges
    }
  }
})
</script>

<style scoped>
.pipeline-status {
  padding: 20px;
}

.pipeline-section-header {
  background-color: #f0eff5 !important;
  color: #281e78 !important;
  font-family: 'Geometria', Arial, Helvetica, sans-serif;
  font-weight: 600;
  font-size: 16px;
  border-bottom: 2px solid #d8d5e8;
}

.v-data-table {
  width: 100%;
}

code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
}

.details-panel {
  border-top: 1px solid #e0e0e0;
}

.work-item-row,
.commit-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.work-item-row:hover,
.commit-row:hover {
  background-color: #e3f2fd;
}

.commit-message {
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

/* Deployment cards */
.deployment-card {
  transition: box-shadow 0.2s ease;
}

.deployment-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

/* Expanded row styling */
::v-deep .v-data-table__expanded__content {
  box-shadow: none !important;
}

::v-deep .v-data-table__expand-icon {
  cursor: pointer;
}
</style>
